// src/pages/admin/components/MapPicker.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { loadYmaps } from '../../../utils/ymapsLoader';

/** Простой пикер: поиск, клик по карте, перетаскивание метки */
export default function MapPicker({
  value = {},               // { lat, lng, address }
  onChange,                 // (next) => void
  lang = 'ru_RU',
  apiKey,                   // можно не передавать — возьмётся из REACT_APP_YMAPS_KEY
  height = 360,
  zoom = 14,
}) {
  const wrapRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const placemarkRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const [addr, setAddr] = React.useState(value.address || '');
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [mapLoading, setMapLoading] = React.useState(true);
  const [mapError, setMapError] = React.useState(null);

  const setPoint = React.useCallback((lat, lng, address) => {
    if (!mapRef.current) return;
    
    // Используем ymaps из window только если он точно загружен
    const ymaps = window.ymaps;
    if (!ymaps || !ymaps.Placemark) {
      console.warn('Яндекс.Карты не готовы для создания метки');
      return;
    }
    
    const coords = [lat, lng];

    if (!placemarkRef.current) {
      placemarkRef.current = new ymaps.Placemark(coords, {}, {
        preset: 'islands#redIcon',
        draggable: true
      });
      placemarkRef.current.events.add('dragend', async () => {
        const c = placemarkRef.current.geometry.getCoordinates();
        // При перетаскивании тоже получаем адрес
        try {
          const g = await ymaps.geocode(c);
          const first = g.geoObjects.get(0);
          const newAddress = first?.getAddressLine?.() || '';
          setAddr(newAddress);
          onChange?.({ lat: c[0], lng: c[1], address: newAddress });
        } catch (error) {
          console.error('Ошибка геокодирования при перетаскивании:', error);
          onChange?.({ lat: c[0], lng: c[1], address: addr });
        }
      });
      mapRef.current.geoObjects.add(placemarkRef.current);
    } else {
      placemarkRef.current.geometry.setCoordinates(coords);
    }
    
    try { 
      mapRef.current.setCenter(coords, zoom, { duration: 200 }); 
    } catch (error) {
      console.error('Ошибка установки центра карты:', error);
    }
    
    // Обновляем локальное состояние адреса
    if (address !== undefined) {
      setAddr(address);
    }
    
    onChange?.({ lat, lng, address: address ?? addr });
  }, [addr, onChange, zoom]);

  // Поиск адресов с автодополнением
  const searchAddresses = React.useCallback(async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    
    try {
      // Ждем загрузки ymaps
      const ymaps = await loadYmaps(apiKey, lang);
      if (!ymaps || !ymaps.geocode) {
        console.warn('Яндекс.Карты не загружены');
        return;
      }
      
      const results = await ymaps.geocode(query, { results: 5 });
      const items = [];
      
      results.geoObjects.each((geoObject) => {
        const coords = geoObject.geometry.getCoordinates();
        const address = geoObject.getAddressLine();
        items.push({ address, coords });
      });
      
      setSuggestions(items);
      setShowSuggestions(items.length > 0);
    } catch (error) {
      console.error('Ошибка поиска адресов:', error);
      setSuggestions([]);
    }
  }, [apiKey, lang]);

  // Дебаунс для поиска
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (addr.length > 2) {
        searchAddresses(addr);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [addr, searchAddresses]);

  React.useEffect(() => {
    let destroyed = false;
    setMapLoading(true);
    setMapError(null);
    
    (async () => {
      try {
        const ymaps = await loadYmaps(apiKey, lang);
        if (destroyed || !wrapRef.current) return;
        
        const center = (value.lat && value.lng) ? [value.lat, value.lng] : [55.751244, 37.618423];

        mapRef.current = new ymaps.Map(wrapRef.current, {
          center, zoom,
          controls: ['zoomControl', 'fullscreenControl']
        }, { suppressMapOpenBlock: true });

        // Клик по карте с геокодированием
        mapRef.current.events.add('click', async (e) => {
          const coords = e.get('coords');
          try {
            const g = await ymaps.geocode(coords);
            const first = g.geoObjects.get(0);
            const address = first?.getAddressLine?.() || '';
            setPoint(coords[0], coords[1], address);
          } catch (error) {
            console.error('Ошибка геокодирования:', error);
            // Все равно ставим метку, но без адреса
            setPoint(coords[0], coords[1], '');
          }
        });

        // Стартовая метка
        if (value.lat && value.lng) {
          setPoint(value.lat, value.lng, value.address);
        }

        setTimeout(() => { 
          try { 
            mapRef.current.container.fitToViewport(); 
          } catch (error) {
            console.error('Ошибка fitToViewport:', error);
          }
        }, 50);
        
        setMapLoading(false);
      } catch (error) {
        console.error('Ошибка инициализации карты:', error);
        setMapError(error.message || 'Не удалось загрузить карту');
        setMapLoading(false);
      }
    })();

    return () => {
      destroyed = true;
      try { 
        if (mapRef.current) {
          mapRef.current.destroy();
          mapRef.current = null;
        }
      } catch (error) {
        console.error('Ошибка при уничтожении карты:', error);
      }
    };
  }, []); // init once

  // Геокод по кнопке
  const geocodeAddress = async () => {
    if (!addr.trim()) return;
    
    try {
      // Ждем загрузки ymaps
      const ymaps = await loadYmaps(apiKey, lang);
      if (!ymaps || !ymaps.geocode) {
        alert('Яндекс.Карты не загружены');
        return;
      }
      
      const res = await ymaps.geocode(addr);
      const first = res.geoObjects.get(0);
      if (!first) {
        alert('Адрес не найден');
        return;
      }
      const c = first.geometry.getCoordinates();
      const address = first.getAddressLine?.() || addr;
      setPoint(c[0], c[1], address);
      setShowSuggestions(false);
    } catch (error) {
      console.error('Ошибка геокодирования:', error);
      alert('Ошибка поиска адреса: ' + (error.message || 'Неизвестная ошибка'));
    }
  };

  // Выбор из подсказок
  const selectSuggestion = (suggestion) => {
    setAddr(suggestion.address);
    setPoint(suggestion.coords[0], suggestion.coords[1], suggestion.address);
    setShowSuggestions(false);
  };

  // Синхронизация с внешним значением
  React.useEffect(() => {
    if (value.address !== addr) {
      setAddr(value.address || '');
    }
  }, [value.address]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display:'flex', gap:8, marginBottom:8 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={searchRef}
            value={addr}
            onChange={(e) => {
              const newAddr = e.target.value;
              setAddr(newAddr);
              onChange?.({ ...value, address: newAddr });
            }}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Введите адрес для поиска"
            style={{ width: '100%' }}
          />
          
          {/* Выпадающий список подсказок */}
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid var(--bdr)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => selectSuggestion(suggestion)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: index < suggestions.length - 1 ? '1px solid #eee' : 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {suggestion.address}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <button type="button" className="btn" onClick={geocodeAddress}>
          Найти на карте
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => value.lat && value.lng && mapRef.current?.setCenter([value.lat, value.lng], zoom)}
        >
          К метке
        </button>
      </div>
      
      {/* Контейнер карты с индикатором загрузки */}
      <div style={{ position: 'relative' }}>
        <div ref={wrapRef} style={{ 
          width:'100%', 
          height, 
          border:'1px solid var(--bdr)', 
          borderRadius:12, 
          overflow:'hidden',
          backgroundColor: mapLoading ? '#f5f5f5' : 'transparent'
        }} />
        
        {mapLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.8)',
            fontSize: 14,
            color: '#666'
          }}>
            Загрузка карты...
          </div>
        )}
        
        {mapError && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.9)',
            fontSize: 14,
            color: '#e66',
            textAlign: 'center',
            padding: 20
          }}>
            <div>
              <div>Ошибка загрузки карты:</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>{mapError}</div>
            </div>
          </div>
        )}
      </div>
      
      {value.lat && value.lng && (
        <div style={{ 
          marginTop: 8, 
          fontSize: 12, 
          color: '#666',
          display: 'flex',
          gap: 16
        }}>
          <span>Широта: {Number(value.lat).toFixed(6)}</span>
          <span>Долгота: {Number(value.lng).toFixed(6)}</span>
        </div>
      )}
    </div>
  );
}

MapPicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  lang: PropTypes.string,
  apiKey: PropTypes.string,
  height: PropTypes.number,
  zoom: PropTypes.number,
};
